import { API_ENDPOINTS } from '../config/api';

export interface ModerationStats {
  trainingReviews: {
    total: number;
    approved: number;
    flagged: number;
    rejected: number;
    pending: number;
  };
  trainingCenterReviews: {
    total: number;
    approved: number;
    flagged: number;
    rejected: number;
    pending: number;
  };
}

export interface TrainingReview {
  id: number;
  trainingId: number;
  trainingTitle: string;
  userId: number;
  userName: string;
  userEmail: string;
  rating: number;
  comment: string;
  helpfulCount: number;
  isVerified: boolean;
  isApproved: boolean;
  isFlagged: boolean;
  moderationNotes?: string;
  moderatedBy?: number;
  moderatedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface TrainingCenterReview {
  id: number;
  trainingCenterId: number;
  trainingCenterName: string;
  userId: number;
  userName: string;
  userEmail: string;
  rating: number;
  comment: string;
  helpfulCount: number;
  isVerified: boolean;
  isApproved: boolean;
  isFlagged: boolean;
  moderationNotes?: string;
  moderatedBy?: number;
  moderatedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ModerationResponse {
  reviews: TrainingReview[] | TrainingCenterReview[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface ModerationAction {
  action: 'approve' | 'reject' | 'flag';
  notes?: string;
  adminId: number;
}

class ModerationService {
  private readonly defaultHeaders = { 'Content-Type': 'application/json' };

  async getModerationStats(): Promise<ModerationStats> {
    const response = await fetch(`${API_ENDPOINTS.BASE_URL}/admin/moderation/stats`, {
      credentials: 'include',
      headers: this.defaultHeaders,
    });

    if (!response.ok) {
      throw new Error('Failed to fetch moderation stats');
    }

    return response.json();
  }

  async getTrainingReviews(
    page: number = 1,
    limit: number = 10,
    status?: 'all' | 'approved' | 'pending' | 'flagged' | 'rejected',
    search?: string
  ): Promise<ModerationResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (status && status !== 'all') {
      params.append('status', status);
    }

    if (search) {
      params.append('search', search);
    }

    const response = await fetch(
      `${API_ENDPOINTS.BASE_URL}/admin/moderation/training-reviews?${params}`,
      {
        credentials: 'include',
        headers: this.defaultHeaders,
      }
    );

    if (!response.ok) {
      throw new Error('Failed to fetch training reviews');
    }

    return response.json();
  }

  async getTrainingCenterReviews(
    page: number = 1,
    limit: number = 10,
    status?: 'all' | 'approved' | 'pending' | 'flagged' | 'rejected',
    search?: string
  ): Promise<ModerationResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (status && status !== 'all') {
      params.append('status', status);
    }

    if (search) {
      params.append('search', search);
    }

    const response = await fetch(
      `${API_ENDPOINTS.BASE_URL}/admin/moderation/training-center-reviews?${params}`,
      {
        credentials: 'include',
        headers: this.defaultHeaders,
      }
    );

    if (!response.ok) {
      throw new Error('Failed to fetch training center reviews');
    }

    return response.json();
  }

  async moderateTrainingReview(
    reviewId: number,
    action: ModerationAction
  ): Promise<{ success: boolean; message: string }> {
    const response = await fetch(
      `${API_ENDPOINTS.BASE_URL}/admin/moderation/training-reviews/${reviewId}/moderate`,
      {
        method: 'POST',
        credentials: 'include',
        headers: this.defaultHeaders,
        body: JSON.stringify(action),
      }
    );

    if (!response.ok) {
      throw new Error('Failed to moderate training review');
    }

    return response.json();
  }

  async moderateTrainingCenterReview(
    reviewId: number,
    action: ModerationAction
  ): Promise<{ success: boolean; message: string }> {
    const response = await fetch(
      `${API_ENDPOINTS.BASE_URL}/admin/moderation/training-center-reviews/${reviewId}/moderate`,
      {
        method: 'POST',
        credentials: 'include',
        headers: this.defaultHeaders,
        body: JSON.stringify(action),
      }
    );

    if (!response.ok) {
      throw new Error('Failed to moderate training center review');
    }

    return response.json();
  }

  async deleteTrainingReview(reviewId: number): Promise<{ success: boolean; message: string }> {
    const response = await fetch(
      `${API_ENDPOINTS.BASE_URL}/admin/moderation/training-reviews/${reviewId}`,
      {
        method: 'DELETE',
        credentials: 'include',
        headers: this.defaultHeaders,
      }
    );

    if (!response.ok) {
      throw new Error('Failed to delete training review');
    }

    return response.json();
  }

  async deleteTrainingCenterReview(reviewId: number): Promise<{ success: boolean; message: string }> {
    const response = await fetch(
      `${API_ENDPOINTS.BASE_URL}/admin/moderation/training-center-reviews/${reviewId}`,
      {
        method: 'DELETE',
        credentials: 'include',
        headers: this.defaultHeaders,
      }
    );

    if (!response.ok) {
      throw new Error('Failed to delete training center review');
    }

    return response.json();
  }
}

export default new ModerationService();
